import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { tableState } from "../lib";

const WRITE_DEBOUNCE_MS = 300;

/** The set of valid leaf-column ids for the current columns. */
const columnIdSet = (columns) => {
    const ids = new Set();
    (columns || []).forEach((c) => {
        const id = c.id ?? c.accessorKey;
        if (id) ids.add(id);
    });
    return ids;
};

const isDisplayId = (id) => typeof id === "string" && id.startsWith("mrt-");

/**
 * Drop persisted state that references columns which no longer exist, so a
 * schema change can never leave a filter/sort/order pointing at a dead column.
 */
const sanitize = (persisted, columns) => {
    const ids = columnIdSet(columns);
    const keep = (id) => ids.has(id) || isDisplayId(id);

    const columnFilters = (persisted?.columnFilters ?? []).filter((f) =>
        ids.has(f.id)
    );
    const sorting = (persisted?.sorting ?? []).filter((s) => ids.has(s.id));

    const columnVisibility = Object.fromEntries(
        Object.entries(persisted?.columnVisibility ?? {}).filter(([id]) =>
            ids.has(id)
        )
    );

    // Keep a persisted order only if it still covers exactly the current data
    // columns (pure reordering). If columns were added/removed, reset to []
    // and let MRT compute the default order.
    const persistedOrder = persisted?.columnOrder ?? [];
    const persistedDataIds = persistedOrder.filter((id) => !isDisplayId(id));
    const sameColumnSet =
        persistedDataIds.length === ids.size &&
        persistedDataIds.every((id) => ids.has(id));
    const columnOrder = sameColumnSet ? persistedOrder : [];

    const pinning = persisted?.columnPinning ?? {};
    const columnPinning = {
        left: (pinning.left ?? []).filter(keep),
        right: (pinning.right ?? []).filter(keep)
    };

    return {
        columnFilters,
        globalFilter: persisted?.globalFilter ?? "",
        sorting,
        columnVisibility,
        columnOrder,
        columnPinning
    };
};

/** Read storage for `key` and fall back to the wrapper's initialState. */
const buildSeed = (key, orgId, columns, defaults = {}) => {
    const stored = key ? tableState.readTableState(key, orgId) : null;
    const clean = sanitize(stored, columns);
    return {
        columnFilters: clean.columnFilters,
        globalFilter: clean.globalFilter,
        sorting: clean.sorting.length ? clean.sorting : defaults.sorting ?? [],
        columnVisibility: Object.keys(clean.columnVisibility).length
            ? clean.columnVisibility
            : defaults.columnVisibility ?? {},
        columnOrder: clean.columnOrder,
        columnPinning:
            clean.columnPinning.left.length || clean.columnPinning.right.length
                ? clean.columnPinning
                : defaults.columnPinning ?? {}
    };
};

/**
 * Controlled + persisted Material React Table state.
 *
 * Two things make this more than a thin localStorage wrapper:
 *
 * 1. MRT calls the same `on*Change` handlers we use for user edits when it
 *    adjusts state internally — it recomputes `columnOrder` on mount and
 *    whenever the column count changes, and it clears `sorting` while it is
 *    fuzzy-ranking global-search results. Those are not user intent, so they
 *    must not be written to storage. `readyRef` ignores anything MRT does
 *    while starting up, and sorting changes are ignored while a global filter
 *    is active (MRT owns sorting in that state, and resets it on every search
 *    change — persisting that would erase the sort the user chose).
 *
 * 2. `persistKey` can change while the component stays mounted — the audit
 *    Review table swaps its whole column set when the audit status changes.
 *    The seed is therefore re-read whenever the key or the org changes, in the
 *    same render, so the table never shows the previous view's filters.
 *
 * @param {string} [persistKey] stable per-table key; when absent, persistence
 *   is disabled and the hook is inert (table behaves exactly as before).
 * @param {Array} columns current MRT column defs (used to prune stale state).
 * @param {Object} [defaults] fallback values (from the wrapper's initialState)
 *   for sorting / columnVisibility / columnPinning.
 * @returns {{ persisted: boolean, slices: Object, handlers: Object }}
 */
export default (persistKey, columns, defaults = {}) => {
    const orgId = useSelector((state) => state.userOrganisations?.item?.id);
    const enabled = !!persistKey;

    const signature = `${persistKey ?? ""}::${orgId ?? ""}`;

    const columnsRef = useRef(columns);
    const defaultsRef = useRef(defaults);
    columnsRef.current = columns;
    defaultsRef.current = defaults;

    const pendingRef = useRef({ key: persistKey, org: orgId, patch: {} });
    const timerRef = useRef(null);
    const readyRef = useRef(false);

    const [seeded, setSeeded] = useState(() => ({
        signature,
        values: buildSeed(persistKey, orgId, columns, defaults)
    }));

    let values = seeded.values;
    if (seeded.signature !== signature) {
        readyRef.current = false;
        values = buildSeed(
            persistKey,
            orgId,
            columnsRef.current,
            defaultsRef.current
        );
        setSeeded({ signature, values });
    }

    const valuesRef = useRef(values);
    valuesRef.current = values;

    const flush = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        const { key, org, patch } = pendingRef.current;
        if (!key || Object.keys(patch).length === 0) return;
        pendingRef.current.patch = {};
        tableState.writeTableState(key, patch, org);
    }, []);

    const scheduleFlush = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, WRITE_DEBOUNCE_MS);
    }, [flush]);

    useEffect(() => {
        const pending = pendingRef.current;
        if (pending.key !== persistKey || pending.org !== orgId) {
            flush();
            pendingRef.current = { key: persistKey, org: orgId, patch: {} };
        }
        const open = setTimeout(() => {
            readyRef.current = true;
        }, 0);
        return () => clearTimeout(open);
    }, [signature, persistKey, orgId, flush]);

    useEffect(() => () => flush(), [flush]);

    /**
     * Is this change real user intent, or MRT adjusting its own state?
     */
    const shouldPersist = useCallback((key) => {
        if (!readyRef.current) return false;
        if (key === "sorting" && valuesRef.current.globalFilter) return false;
        return true;
    }, []);

    const makeHandler = useCallback(
        (key) => (updater) => {
            // shouldPersist reads the *pre-change* values (it suppresses sorting
            // writes while a global filter is active), so evaluate it first.
            const persist = shouldPersist(key);

            const current = valuesRef.current[key];
            const next =
                typeof updater === "function" ? updater(current) : updater;
            if (next === current) return;

            // Keep the ref authoritative so a second handler firing in the same
            // tick, before React re-renders, still sees the latest value.
            valuesRef.current = { ...valuesRef.current, [key]: next };

            if (persist) {
                pendingRef.current.patch[key] = next;
                scheduleFlush();
            }

            // The updater stays pure — recording the pending patch above rather
            // than inside it means React can safely re-invoke or discard this
            // state update without writing a change that never happened.
            setSeeded((prev) => ({
                ...prev,
                values: { ...prev.values, [key]: next }
            }));
        },
        [shouldPersist, scheduleFlush]
    );

    const handlers = useMemo(() => {
        if (!enabled) return {};
        return {
            onColumnFiltersChange: makeHandler("columnFilters"),
            onGlobalFilterChange: makeHandler("globalFilter"),
            onSortingChange: makeHandler("sorting"),
            onColumnVisibilityChange: makeHandler("columnVisibility"),
            onColumnOrderChange: makeHandler("columnOrder"),
            onColumnPinningChange: makeHandler("columnPinning")
        };
    }, [enabled, makeHandler]);

    return {
        persisted: enabled,
        slices: enabled ? values : {},
        handlers
    };
};
